import { FormEvent, useEffect, useState } from 'react';
import { Alert, Button, ControlLabel, FormControl, FormGroup, Modal, Radio } from 'react-bootstrap'
import usePrevious from '@nkr/services/use-previous';

interface IEnrollmentData {
    firstName: string,
    lastName: string,
    isExpertSystem: boolean,
    isInExpertSystem: boolean,
    hasHlaAlleleTyping: boolean
}

interface IProps {
    show: boolean,
    data: IEnrollmentData,
    error: string,
    existingDonorAlias: string | null,
    onConfirm: (useBulkKit: number, alias: string) => void,
    onCancel: () => void
}

const ExpertSystemEnrollmentConfirm = (props: IProps) => {
    const [alias, setAlias] = useState('');
    const [useBulkKit, setUseBulkKit] = useState(-1);

    const previouShow = usePrevious(props.show);

    useEffect(() => {
        if (previouShow == false && props.show == true) {
            setAlias('');
            setUseBulkKit(-1);
        }
    }, [props.show])

    const handleAliasChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        setAlias(e.currentTarget.value);
    };

    const handleBulkKitChange = (e: FormEvent<any>) => {
        setUseBulkKit(parseInt(e.currentTarget.value));
    }

    const handleConfirm = () => {
        const effectiveAlias = props.existingDonorAlias != null ? props.existingDonorAlias : alias;
        props.onConfirm(useBulkKit, effectiveAlias);
    };

    if (props.data == null)
        return null;

    const hasExistingProfile = props.existingDonorAlias != null;
    const aliasValid = hasExistingProfile || (alias.length >= 6 && alias.length <= 12);

    const disableSubmit = props.data.isExpertSystem
        ? true
        : !aliasValid || (!props.data.hasHlaAlleleTyping && useBulkKit === -1);

    return (
        <Modal backdrop='static' show={props.show} onHide={props.onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Start Expert System Enrollment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {props.error != '' && (
                    <Alert bsStyle='danger' bsSize='sm'>
                        <div style={{ marginBottom: 5 }}>We were unable to enroll this donor in the Expert System due to the following...</div>
                        <strong>{props.error}</strong>
                    </Alert>
                )}

                <blockquote>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ marginRight: 10 }}>Donor:</div>
                        <div style={{ fontWeight: 'bold', color: 'green' }}>
                            {props.data.firstName + (props.data.lastName ? ' ' + props.data.lastName : '')}
                        </div>
                    </div>
                </blockquote>

                {props.data.isInExpertSystem ?
                    <div>
                        <label className='control-label' style={{ paddingTop: '7px' }}>Cannot Start Expert System Enrollment - Enrollment already exists.</label>
                    </div>
                    :
                    <>
                        {hasExistingProfile && (
                            <Alert bsStyle='success' bsSize='sm'>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <span className='glyphicon glyphicon-ok-circle' style={{ marginTop: 2, flexShrink: 0 }} />
                                    <div>
                                        <strong>Donor profile already exists</strong>
                                        <div>A donor profile has already been created for this donor.</div>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {hasExistingProfile ? (
                            <div style={{ marginBottom: 15 }}>
                                <strong>Donor Alias:</strong>&nbsp;&nbsp;{props.existingDonorAlias}
                            </div>
                        ) : (
                            <FormGroup validationState={alias.length > 0 && !aliasValid ? 'error' : null}>
                                <ControlLabel>
                                    Please specify an alias for the new donor: <span style={{ color: 'red' }}>*</span>
                                </ControlLabel>
                                <FormControl
                                    required
                                    minLength={6}
                                    maxLength={12}
                                    value={alias}
                                    onChange={handleAliasChange}
                                />
                                {alias.length > 0 && alias.length < 6 && (
                                    <span style={{ color: '#a94442', fontSize: 12 }}>Alias must be at least 6 characters.</span>
                                )}
                            </FormGroup>
                        )}

                        {props.data.hasHlaAlleleTyping ?
                            <>
                                <div style={{ marginBottom: 20 }}>
                                    <label htmlFor='typingAlreadyExist'>Typing already on hand, no need to ship a kit.</label>
                                </div>
                                <p>Please click the button below to proceed with Expert System Enrollment</p>
                            </>
                            :
                            <>
                                <label htmlFor='useBulkKit'>
                                    How would you like to obtain the HLA and ABO sample?
                                </label>

                                <div>
                                    <Radio
                                        name='useBulkKit'
                                        id='useBulkKit0'
                                        checked={useBulkKit == 0}
                                        value={0}
                                        onChange={handleBulkKitChange}>
                                        <strong>Ship a kit to the donor</strong>
                                        <div style={{ fontSize: 12, fontWeight: 'normal' }}>Order a kit to be shipped to the donor's home.</div>
                                    </Radio>
                                </div>
                                <div style={{ marginBottom: 20 }}>
                                    <Radio
                                        name='useBulkKit'
                                        id='useBulkKit1'
                                        checked={useBulkKit == 1}
                                        value={1}
                                        onChange={handleBulkKitChange}>
                                        <strong>Use a kit on hand</strong>
                                        <div style={{ fontSize: 12, fontWeight: 'normal' }}>I will use a kit that I have here with the donor.</div>
                                    </Radio>
                                </div>

                                <p>
                                    <strong>Note:</strong> If you select to use a kit on hand, you will be redirected to the bulk kit data entry screen and will need to print the requisition and return it with the kit when this process is complete.
                                </p>
                            </>
                        }
                    </>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' disabled={disableSubmit} onClick={handleConfirm}>Proceed with Expert System Enrollment</Button>
                <Button onClick={props.onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ExpertSystemEnrollmentConfirm;
